import { useSetAtom } from 'jotai'
import _ from 'lodash'
import { DateTime } from 'luxon'
import { useEffect, useMemo, useState } from 'react'
import { ChipInputWithAutocomplete } from 'sierra-client/components/common/chip-input-with-autocomplete'
import { useNotif } from 'sierra-client/components/common/notifications'
import { useAssignUsersAdapter, useUnassignContent } from 'sierra-client/components/sharing/hooks'
import {
  AutocompleteContainer,
  ExistingAssignedUsersContainer,
} from 'sierra-client/components/sharing/tabs/components/containers'
import { LiveSessionPicker } from 'sierra-client/components/sharing/tabs/components/live-session-picker'
import {
  MoreResultsMenuItem,
  SharingModalMenuItem,
  SharingModalMenuItemWithAssignLiveControls,
  SharingModalUser,
  SharingModalUserOrLoadMore,
  isSharingModalUser,
} from 'sierra-client/components/sharing/tabs/components/sharing-modal-menu-item'
import { RenderTags } from 'sierra-client/components/sharing/tabs/utils/render-tags'
import { SharingModalContent } from 'sierra-client/components/sharing/types'
import { useTranslation } from 'sierra-client/hooks/use-translation'
import { useCachedQuery } from 'sierra-client/state/api'
import { fetchLiveSessionsForFlexibleContent } from 'sierra-client/state/flexible-content/actions'
import { useDispatch } from 'sierra-client/state/hooks'
import { isUserLoaded, useUsers } from 'sierra-client/state/users/hooks'
import { manageSessionsPanelStateAtom } from 'sierra-client/views/flexible-content/editor/topbar/manage-sessions-panel-state'
import { useDueDate } from 'sierra-client/views/manage/components/due-date'
import { LiveSessionWithParticipants } from 'sierra-domain/api/admin'
import { IdentityWithMetadata } from 'sierra-domain/api/manage'
import { CreateContentId, LiveContentId } from 'sierra-domain/api/nano-id'
import { XRealtimeAuthorLiveSessionsListLiveSessions } from 'sierra-domain/routes'
import { assertNever, getUserName, guardWith } from 'sierra-domain/utils'
import { Button, View } from 'sierra-ui/primitives'

export const AssignLiveParticipants: React.FC<{
  content: SharingModalContent
  canEditAssignments: boolean
}> = ({ content, canEditAssignments }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const notifications = useNotif()
  const unassignContent = useUnassignContent()
  const setManageSessionsPanelState = useSetAtom(manageSessionsPanelStateAtom)

  const { assignWithDueDate } = useDueDate()

  if (!guardWith(LiveContentId, content.id))
    throw Error('Could not find live content id for assigning to live session')

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUsers, setSelectedUsers] = useState<SharingModalUser[]>([])

  const liveContentId = LiveContentId.parse(content.id)

  const futureSessionsQuery = useCachedQuery(
    XRealtimeAuthorLiveSessionsListLiveSessions,
    {
      flexibleContentId: liveContentId,
    },
    {
      select: data =>
        data.liveSessions.filter(
          session =>
            session.data.type === 'scheduled' && DateTime.fromISO(session.data.endTime) > DateTime.now()
        ),
    }
  )
  const futureSessions = useMemo(() => futureSessionsQuery.data ?? [], [futureSessionsQuery.data])

  const firstSession = useMemo(() => _.first(futureSessions), [futureSessions])
  const [_selectedLiveSession, setSelectedLiveSession] = useState<LiveSessionWithParticipants | undefined>()
  const selectedLiveSession = _selectedLiveSession ?? firstSession

  // After creating a new session, listen to the new session being added, and select it.
  const [newlyCreatedSessionId, setNewlyCreatedSessionId] = useState<string | undefined>()
  useEffect(() => {
    const newlyCreatedSession = futureSessions.find(s => s.liveSessionId === newlyCreatedSessionId)
    if (newlyCreatedSession !== undefined) {
      setSelectedLiveSession(newlyCreatedSession)
      setNewlyCreatedSessionId(undefined)
    }
  }, [newlyCreatedSessionId, futureSessions])

  const onSessionPick = (
    pick:
      | { action: 'schedule-new-session' }
      | { action: 'schedule-existing-session'; session: LiveSessionWithParticipants }
  ): void => {
    switch (pick.action) {
      case 'schedule-new-session':
        setManageSessionsPanelState({
          type: 'create-session',
          initialData: {
            participants: selectedUsers.map(
              user =>
                ({
                  identity: {
                    type: 'user',
                    id: user.uuid,
                  },
                  name: getUserName(user) ?? '',
                  email: user.email,
                  avatar: {
                    type: 'color',
                    initials: `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`,
                    color: user.avatarColor ?? 'redVivid',
                  },
                }) satisfies IdentityWithMetadata
            ),
          },
          onSessionCreated: sessionId => {
            setNewlyCreatedSessionId(sessionId)
            setSelectedUsers([])
            setManageSessionsPanelState({ type: 'closed' })
          },
        })
        break
      case 'schedule-existing-session':
        setSelectedLiveSession(pick.session)
        break
      default:
        assertNever(pick)
    }
  }

  const {
    assignableUsers,
    isLoading: isLoadingAssignableUsers,
    hasMore: hasMoreAssignableUsers,
  } = useAssignUsersAdapter({
    content,
    searchQuery,
    shouldLoadMore: false,
  })

  const assignableItems: SharingModalUserOrLoadMore[] = [...assignableUsers, { type: 'load-more' }]

  const noSelectedUsersOrSession = selectedUsers.length === 0 || selectedLiveSession === undefined

  const onAssignClick = async (): Promise<void> => {
    if (noSelectedUsersOrSession) return
    await assignWithDueDate(
      selectedUsers.map(user => ({
        assignee: {
          type: 'user',
          id: user.uuid,
          dueDate: undefined,
        },
        content: {
          id: selectedLiveSession.liveSessionId,
          type: content.type,
        },
      }))
    ).then(() => {
      void dispatch(fetchLiveSessionsForFlexibleContent({ liveContentId }))
    })

    setSearchQuery('')
    setSelectedUsers([])
    notifications.push({
      type: 'custom',
      level: 'info',
      icon: 'send--filled',
      body: 'Invite sent',
    })
  }

  const participantsToLiveSessions = _.chain(futureSessions)
    .flatMap(session => session.participantIds.map(pId => ({ participantId: pId, session: session })))
    .groupBy('participantId')
    .value()

  const participantUsers = useUsers(
    _.chain(futureSessions)
      .flatMap(session => session.participantIds)
      .uniq()
      .value()
  ).filter(isUserLoaded)

  const allParticipantsWithLiveSessions = participantUsers.map(participant => ({
    ...participant,
    type: 'user' as const,
    liveSessions: participantsToLiveSessions[participant.uuid]?.map(s => s.session) ?? [],
  }))

  return (
    <div>
      <View justifyContent='space-between'>
        <AutocompleteContainer>
          <ChipInputWithAutocomplete<SharingModalUserOrLoadMore, true, true, false>
            ListboxProps={{ style: { pointerEvents: 'auto' } }}
            inputPlaceholder={selectedUsers.length === 0 ? t('admin.add-users') : ''}
            inputValue={searchQuery}
            onInputChange={(_, filter) => setSearchQuery(filter)}
            value={selectedUsers}
            onChange={(_, users) => setSelectedUsers(users.filter(isSharingModalUser))}
            noOptionsText={
              isLoadingAssignableUsers ? t('dictionary.loading') : t('sharing-modal.no-users-found')
            }
            autoFocus
            multiple
            disableClearable
            disabled={!canEditAssignments}
            options={assignableItems}
            getOptionDisabled={user => user.type === 'load-more'}
            getOptionSelected={(user, val) =>
              user.type === 'user' && val.type === 'user' && user.uuid === val.uuid
            }
            getOptionLabel={user =>
              user.type === 'user'
                ? _.compact([user.firstName, user.lastName, user.email]).join(' ')
                : 'load-more'
            }
            renderOption={user => {
              if (user.type === 'load-more') {
                if (searchQuery === '' && !isLoadingAssignableUsers && hasMoreAssignableUsers) {
                  return <MoreResultsMenuItem key='load-more' />
                }
              } else {
                return (
                  <SharingModalMenuItem
                    key={user.uuid}
                    user={user}
                    selected={selectedUsers.some(u => u.uuid === user.uuid)}
                  />
                )
              }
            }}
            renderTags={users => (
              <RenderTags
                selectedUsers={users.filter(isSharingModalUser)}
                setSelectedUsers={setSelectedUsers}
                uuidKey='uuid'
              />
            )}
          />
        </AutocompleteContainer>

        {canEditAssignments && (
          <LiveSessionPicker
            selectedSession={selectedLiveSession}
            sessions={futureSessions}
            onSessionPick={onSessionPick}
            liveContentId={liveContentId}
          />
        )}

        <Button disabled={noSelectedUsersOrSession} onClick={onAssignClick}>
          {t('dictionary.assign')}
        </Button>
      </View>
      <ExistingAssignedUsersContainer>
        {allParticipantsWithLiveSessions.map(participant => (
          <SharingModalMenuItemWithAssignLiveControls
            key={participant.uuid}
            user={participant}
            onUnassign={
              canEditAssignments
                ? async () => {
                    await Promise.all(
                      participant.liveSessions.map(async session => {
                        await unassignContent(participant.uuid, {
                          ...content,
                          id: CreateContentId.parse(session.liveSessionId),
                        })
                      })
                    ).then((): void => {
                      void futureSessionsQuery.refetch()
                    })
                  }
                : undefined
            }
          />
        ))}
      </ExistingAssignedUsersContainer>
    </div>
  )
}
